import React, { useState } from 'react';
import { 
  Type, 
  Square, 
  Circle, 
  Minus, 
  Image as ImageIcon, 
  Sliders, 
  Plus, 
  AlignLeft, 
  AlignCenter, 
  AlignRight, 
  AlignJustify,
  Bold, 
  Italic, 
  Underline,
  Palette,
  Upload,
  Layers,
  Sparkles,
  Braces,
  Search,
  Check,
  Copy,
  User,
  Truck,
  FileText,
  DollarSign,
  Clock,
  ShieldCheck,
  FileCheck,
  Fingerprint,
  Globe,
  AlertTriangle
} from 'lucide-react';
import { TOKENS_DOCUMENTO, TokenDocumento } from '../../services/tokensDocumento';

interface Props {
  onAddText: (text: string, options?: any) => void;
  onAddRect: () => void;
  onAddCircle: () => void;
  onAddLine: () => void;
  onAddImage: (url: string) => void;
  selectedProps: Record<string, any> | null;
  onUpdateStyle: (styleProps: Record<string, any>) => void;
}

// Catálogo único de tokens (compartilhado com o motor de geração de PDF e com o
// validador). Antes esta lista era local aqui, o que fez o editor oferecer
// campos que o sistema não sabia preencher.
type CampoAtalho = TokenDocumento;

const ATALHOS_CAMPOS: CampoAtalho[] = TOKENS_DOCUMENTO;

export const SidebarElementos: React.FC<Props> = ({
  onAddText,
  onAddRect,
  onAddCircle,
  onAddLine,
  onAddImage,
  selectedProps,
  onUpdateStyle
}) => {
  const [activeTab, setActiveTab] = useState<'campos' | 'elementos' | 'propriedades'>('campos');
  const [categoriaFiltro, setCategoriaFiltro] = useState<string>('todas');
  const [searchCampo, setSearchCampo] = useState('');
  const [copiedTag, setCopiedTag] = useState<string | null>(null);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          onAddImage(event.target.result as string);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCopyTag = (tag: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(tag);
    setCopiedTag(tag);
    setTimeout(() => setCopiedTag(null), 1500);
  };

  const filteredCampos = ATALHOS_CAMPOS.filter((c) => {
    const matchCategoria = categoriaFiltro === 'todas' || c.categoria === categoriaFiltro;
    const matchText =
      c.label.toLowerCase().includes(searchCampo.toLowerCase()) ||
      c.tag.toLowerCase().includes(searchCampo.toLowerCase()) ||
      c.exemplo.toLowerCase().includes(searchCampo.toLowerCase());
    return matchCategoria && matchText;
  });

  return (
    <div className="w-80 bg-white border-r border-slate-200 flex flex-col shrink-0 h-full select-none">
      {/* Tabs */}
      <div className="flex border-b border-slate-200 bg-slate-50 p-1 gap-1">
        <button
          onClick={() => setActiveTab('campos')}
          className={`flex-1 py-2 text-xs font-bold rounded-lg flex items-center justify-center gap-1 transition ${
            activeTab === 'campos'
              ? 'bg-white text-slate-900 shadow-sm border border-slate-200/80'
              : 'text-slate-500 hover:text-slate-800'
          }`}
        >
          <Braces className="w-3.5 h-3.5 text-red-600" />
          <span>Campos</span>
        </button>

        <button
          onClick={() => setActiveTab('elementos')}
          className={`flex-1 py-2 text-xs font-bold rounded-lg flex items-center justify-center gap-1 transition ${
            activeTab === 'elementos'
              ? 'bg-white text-slate-900 shadow-sm border border-slate-200/80'
              : 'text-slate-500 hover:text-slate-800'
          }`}
        >
          <Plus className="w-3.5 h-3.5 text-red-600" />
          <span>Elementos</span>
        </button>

        <button
          onClick={() => setActiveTab('propriedades')}
          className={`flex-1 py-2 text-xs font-bold rounded-lg flex items-center justify-center gap-1 transition relative ${
            activeTab === 'propriedades'
              ? 'bg-white text-slate-900 shadow-sm border border-slate-200/80'
              : 'text-slate-500 hover:text-slate-800'
          }`}
        >
          <Sliders className="w-3.5 h-3.5 text-red-600" />
          <span>Estilo</span>
          {selectedProps && (
            <span className="w-2 h-2 rounded-full bg-red-600 absolute top-2 right-2 animate-ping" />
          )}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {activeTab === 'campos' ? (
          /* TABA DE ATALHOS PARA CAMPOS E VARIÁVEIS DINÂMICAS */
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                  <Braces className="w-4 h-4 text-red-600" />
                  Atalhos de Campos
                </span>
                <span className="text-[10px] bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-bold">
                  {filteredCampos.length} tags
                </span>
              </div>
              <p className="text-[11px] text-slate-500">
                Clique em qualquer campo abaixo para inseri-lo diretamente no modelo do documento.
              </p>
            </div>

            {/* Campo Busca */}
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-2.5" />
              <input
                type="text"
                placeholder="Buscar campo (ex: assinatura, rg, ip, cliente...)"
                value={searchCampo}
                onChange={(e) => setSearchCampo(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-xs border border-slate-200 rounded-xl bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
              />
            </div>

            {/* Filtros por Categoria (Chips) */}
            <div className="flex flex-wrap gap-1 pb-1">
              {[
                { id: 'todas', label: 'Todas' },
                { id: 'assinatura', label: '✍️ Assinatura / RG / IP' },
                { id: 'os', label: '📄 OS' },
                { id: 'cliente', label: '👤 Cliente' },
                { id: 'veiculo', label: '🚚 Veículo' },
                { id: 'financeiro', label: '💲 Financeiro' },
                { id: 'proposta', label: '📋 Proposta' },
              ].map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setCategoriaFiltro(cat.id)}
                  className={`text-[10px] px-2 py-1 rounded-lg font-bold transition ${
                    categoriaFiltro === cat.id
                      ? 'bg-red-600 text-white shadow-xs'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>

            {/* Lista de Campos */}
            <div className="space-y-2">
              {filteredCampos.length === 0 ? (
                <div className="text-center py-6 text-slate-400 text-xs">
                  Nenhum campo encontrado para os filtros selecionados.
                </div>
              ) : (
                filteredCampos.map((item) => (
                  <div
                    key={item.tag}
                    onClick={() =>
                      onAddText(item.tag, {
                        fontSize: 12,
                        fontWeight: 'bold',
                        fill: '#1e293b',
                        fontFamily: 'Arial',
                      })
                    }
                    className="group border border-slate-200 hover:border-red-500 hover:bg-red-50/20 rounded-xl p-2.5 cursor-pointer transition flex items-center justify-between shadow-xs"
                  >
                    <div className="space-y-0.5 min-w-0 pr-2">
                      <div className="flex items-center gap-1.5">
                        {item.categoria === 'assinatura' && (
                          <ShieldCheck className="w-3.5 h-3.5 text-red-600 shrink-0" />
                        )}
                        <span className="text-xs font-bold text-slate-800 group-hover:text-red-700 truncate">
                          {item.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <code className="text-[10px] bg-slate-100 group-hover:bg-red-100/60 text-slate-600 group-hover:text-red-800 px-1.5 py-0.5 rounded font-mono font-bold">
                          {item.tag}
                        </code>
                        <span className="text-[10px] text-slate-400 truncate">
                          Ex: {item.exemplo}
                        </span>
                      </div>

                      {item.disponivel === 'indisponivel' ? (
                        <div
                          className="flex items-start gap-1 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-1 mt-1"
                          title={item.observacao}
                        >
                          <AlertTriangle className="w-3 h-3 text-amber-500 shrink-0 mt-px" />
                          <span className="leading-snug">
                            Sem dado no sistema — sairá em branco no documento.
                          </span>
                        </div>
                      ) : item.disponivel !== 'ambos' ? (
                        <span className="text-[10px] text-slate-400 mt-0.5 block">
                          Preenchido apenas em: {item.disponivel === 'os' ? 'Ordem de Serviço' : 'Proposta'}
                        </span>
                      ) : null}
                    </div>

                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={(e) => handleCopyTag(item.tag, e)}
                        className="p-1.5 hover:bg-white text-slate-400 hover:text-slate-700 rounded-lg transition"
                        title="Copiar apenas a Tag"
                      >
                        {copiedTag === item.tag ? (
                          <Check className="w-3.5 h-3.5 text-emerald-600" />
                        ) : (
                          <Copy className="w-3.5 h-3.5" />
                        )}
                      </button>
                      <button
                        className="p-1.5 bg-red-600 text-white rounded-lg opacity-0 group-hover:opacity-100 transition shadow-xs"
                        title="Inserir no Canvas"
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        ) : activeTab === 'elementos' ? (
          <>
            {/* SEÇÃO TEXTO */}
            <div className="space-y-3">
              <div className="flex items-center gap-1.5 text-xs font-bold text-slate-800 uppercase tracking-wider">
                <Type className="w-4 h-4 text-red-600" />
                <span>Textos</span>
              </div>

              <div className="space-y-2">
                <button
                  onClick={() => onAddText('TÍTULO PRINCIPAL', { fontSize: 24, fontWeight: 'bold' })}
                  className="w-full text-left p-3 rounded-xl border border-slate-200 hover:border-red-500 hover:bg-red-50/20 transition group"
                >
                  <span className="font-extrabold text-lg text-slate-900 group-hover:text-red-700 block">
                    Adicionar Título
                  </span>
                  <span className="text-[11px] text-slate-400">Fonte 24px • Negrito</span>
                </button>

                <button
                  onClick={() => onAddText('Subtítulo do Documento', { fontSize: 16, fontWeight: 'bold', fill: '#475569' })}
                  className="w-full text-left p-2.5 rounded-xl border border-slate-200 hover:border-red-500 hover:bg-red-50/20 transition group"
                >
                  <span className="font-bold text-base text-slate-700 group-hover:text-red-700 block">
                    Adicionar Subtítulo
                  </span>
                  <span className="text-[11px] text-slate-400">Fonte 16px</span>
                </button>

                <button
                  onClick={() => onAddText('Insira o texto de corpo do documento aqui...', { fontSize: 12 })}
                  className="w-full text-left p-2.5 rounded-xl border border-slate-200 hover:border-red-500 hover:bg-red-50/20 transition group"
                >
                  <span className="font-normal text-xs text-slate-600 group-hover:text-red-700 block">
                    Adicionar Texto de Corpo
                  </span>
                  <span className="text-[11px] text-slate-400">Fonte 12px</span>
                </button>
              </div>
            </div>

            <hr className="border-slate-100" />

            {/* SEÇÃO FORMAS */}
            <div className="space-y-3">
              <div className="flex items-center gap-1.5 text-xs font-bold text-slate-800 uppercase tracking-wider">
                <Square className="w-4 h-4 text-red-600" />
                <span>Formas & Linhas</span>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={onAddRect}
                  className="flex flex-col items-center justify-center p-3 rounded-xl border border-slate-200 hover:border-red-500 hover:bg-red-50/20 transition gap-2"
                >
                  <Square className="w-6 h-6 text-slate-700" />
                  <span className="text-xs font-semibold text-slate-700">Retângulo</span>
                </button>

                <button
                  onClick={onAddCircle}
                  className="flex flex-col items-center justify-center p-3 rounded-xl border border-slate-200 hover:border-red-500 hover:bg-red-50/20 transition gap-2"
                >
                  <Circle className="w-6 h-6 text-slate-700" />
                  <span className="text-xs font-semibold text-slate-700">Círculo</span>
                </button>

                <button
                  onClick={onAddLine}
                  className="flex flex-col items-center justify-center p-3 rounded-xl border border-slate-200 hover:border-red-500 hover:bg-red-50/20 transition gap-2"
                >
                  <Minus className="w-6 h-6 text-slate-700" />
                  <span className="text-xs font-semibold text-slate-700">Linha</span>
                </button>
              </div>
            </div>

            <hr className="border-slate-100" />

            {/* SEÇÃO IMAGEM */}
            <div className="space-y-3">
              <div className="flex items-center gap-1.5 text-xs font-bold text-slate-800 uppercase tracking-wider">
                <ImageIcon className="w-4 h-4 text-red-600" />
                <span>Imagens & Logo</span>
              </div>

              <label className="flex flex-col items-center justify-center p-4 rounded-xl border-2 border-dashed border-slate-200 hover:border-red-500 hover:bg-red-50/20 cursor-pointer transition text-center gap-2">
                <Upload className="w-6 h-6 text-slate-400" />
                <div>
                  <span className="text-xs font-bold text-slate-800 block">Enviar Imagem / Logo</span>
                  <span className="text-[10px] text-slate-400">PNG, JPG, SVG</span>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageUpload}
                />
              </label>

              {/* Logo VOOPE pre-set */}
              <button
                onClick={() => onAddImage('/logo.png')}
                className="w-full py-2 px-3 rounded-xl border border-slate-200 hover:border-red-500 hover:bg-red-50/20 text-xs font-bold text-slate-700 flex items-center justify-center gap-2 transition"
              >
                <Sparkles className="w-3.5 h-3.5 text-red-600" />
                Inserir Logo VOOPE Padrão
              </button>
            </div>
          </>
        ) : (
          /* PROPRIEDADES DO ELEMENTO SELECIONADO */
          <div className="space-y-5">
            {!selectedProps ? (
              <div className="text-center py-10 text-slate-400 space-y-2">
                <Sliders className="w-8 h-8 mx-auto opacity-40" />
                <p className="text-xs font-medium">Nenhum elemento selecionado no canvas.</p>
                <p className="text-[11px] text-slate-400">Clique em qualquer objeto na página para alterar suas propriedades.</p>
              </div>
            ) : (
              <>
                <div className="p-2.5 bg-slate-100 rounded-xl text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center justify-between">
                  <span>Elemento: {selectedProps.type}</span>
                </div>

                {/* COR DE PREENCHIMENTO */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700 flex items-center justify-between">
                    <span>Cor de Preenchimento</span>
                    <span className="font-mono text-[11px] text-slate-400">{selectedProps.fill}</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={typeof selectedProps.fill === 'string' ? selectedProps.fill : '#000000'}
                      onChange={(e) => onUpdateStyle({ fill: e.target.value })}
                      className="w-10 h-10 rounded-lg cursor-pointer border border-slate-300 p-0.5 bg-white"
                    />
                    <input
                      type="text"
                      value={selectedProps.fill || ''}
                      onChange={(e) => onUpdateStyle({ fill: e.target.value })}
                      className="flex-1 text-xs border border-slate-300 rounded-lg px-2.5 py-2 font-mono"
                      placeholder="#000000"
                    />
                  </div>
                </div>

                {/* BORDA / STROKE */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700 flex items-center justify-between">
                    <span>Cor da Borda</span>
                    <span className="font-mono text-[11px] text-slate-400">{selectedProps.stroke}</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={typeof selectedProps.stroke === 'string' ? selectedProps.stroke : '#000000'}
                      onChange={(e) => onUpdateStyle({ stroke: e.target.value })}
                      className="w-10 h-10 rounded-lg cursor-pointer border border-slate-300 p-0.5 bg-white"
                    />
                    <input
                      type="number"
                      min="0"
                      max="20"
                      value={selectedProps.strokeWidth || 0}
                      onChange={(e) => onUpdateStyle({ strokeWidth: Number(e.target.value) })}
                      className="w-20 text-xs border border-slate-300 rounded-lg px-2.5 py-2"
                      placeholder="Espessura"
                    />
                  </div>
                </div>

                {/* PROPRIEDADES EXCLUSIVAS DE TEXTO */}
                {(selectedProps.type === 'i-text' || selectedProps.type === 'textbox' || selectedProps.type === 'text') && (
                  <>
                    <hr className="border-slate-100" />

                    {/* FONTE & TAMANHO */}
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-slate-700 block">Fonte & Tamanho</label>
                      <div className="grid grid-cols-2 gap-2">
                        <select
                          value={selectedProps.fontFamily || 'Arial'}
                          onChange={(e) => onUpdateStyle({ fontFamily: e.target.value })}
                          className="text-xs border border-slate-300 rounded-lg p-2 bg-white"
                        >
                          <option value="Arial">Arial</option>
                          <option value="Helvetica">Helvetica</option>
                          <option value="Times New Roman">Times New Roman</option>
                          <option value="Courier">Courier</option>
                          <option value="Georgia">Georgia</option>
                          <option value="Trebuchet MS">Trebuchet MS</option>
                          <option value="Verdana">Verdana</option>
                          <option value="Impact">Impact</option>
                        </select>

                        <input
                          type="number"
                          min="8"
                          max="120"
                          value={selectedProps.fontSize || 16}
                          onChange={(e) => onUpdateStyle({ fontSize: Number(e.target.value) })}
                          className="text-xs border border-slate-300 rounded-lg p-2"
                          placeholder="Tamanho"
                        />
                      </div>
                    </div>

                    {/* ESTILOS DE TEXTO */}
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-slate-700 block">Estilo do Texto</label>
                      <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg">
                        <button
                          onClick={() => onUpdateStyle({ fontWeight: selectedProps.fontWeight === 'bold' ? 'normal' : 'bold' })}
                          className={`p-2 rounded-md transition flex-1 flex justify-center ${
                            selectedProps.fontWeight === 'bold' ? 'bg-white shadow text-red-600' : 'text-slate-600'
                          }`}
                          title="Negrito"
                        >
                          <Bold className="w-4 h-4" />
                        </button>

                        <button
                          onClick={() => onUpdateStyle({ fontStyle: selectedProps.fontStyle === 'italic' ? 'normal' : 'italic' })}
                          className={`p-2 rounded-md transition flex-1 flex justify-center ${
                            selectedProps.fontStyle === 'italic' ? 'bg-white shadow text-red-600' : 'text-slate-600'
                          }`}
                          title="Itálico"
                        >
                          <Italic className="w-4 h-4" />
                        </button>

                        <button
                          onClick={() => onUpdateStyle({ underline: !selectedProps.underline })}
                          className={`p-2 rounded-md transition flex-1 flex justify-center ${
                            selectedProps.underline ? 'bg-white shadow text-red-600' : 'text-slate-600'
                          }`}
                          title="Sublinhado"
                        >
                          <Underline className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* ALINHAMENTO DO TEXTO */}
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-slate-700 block">Alinhamento</label>
                      <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg">
                        <button
                          onClick={() => onUpdateStyle({ textAlign: 'left' })}
                          className={`p-2 rounded-md transition flex-1 flex justify-center ${
                            selectedProps.textAlign === 'left' ? 'bg-white shadow text-red-600' : 'text-slate-600'
                          }`}
                        >
                          <AlignLeft className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => onUpdateStyle({ textAlign: 'center' })}
                          className={`p-2 rounded-md transition flex-1 flex justify-center ${
                            selectedProps.textAlign === 'center' ? 'bg-white shadow text-red-600' : 'text-slate-600'
                          }`}
                        >
                          <AlignCenter className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => onUpdateStyle({ textAlign: 'right' })}
                          className={`p-2 rounded-md transition flex-1 flex justify-center ${
                            selectedProps.textAlign === 'right' ? 'bg-white shadow text-red-600' : 'text-slate-600'
                          }`}
                        >
                          <AlignRight className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => onUpdateStyle({ textAlign: 'justify' })}
                          className={`p-2 rounded-md transition flex-1 flex justify-center ${
                            selectedProps.textAlign === 'justify' ? 'bg-white shadow text-red-600' : 'text-slate-600'
                          }`}
                        >
                          <AlignJustify className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </>
                )}

                <hr className="border-slate-100" />

                {/* OPACIDADE */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700 flex items-center justify-between">
                    <span>Opacidade</span>
                    <span className="text-xs text-slate-500">{Math.round((selectedProps.opacity || 1) * 100)}%</span>
                  </label>
                  <input
                    type="range"
                    min="0.1"
                    max="1"
                    step="0.05"
                    value={selectedProps.opacity !== undefined ? selectedProps.opacity : 1}
                    onChange={(e) => onUpdateStyle({ opacity: Number(e.target.value) })}
                    className="w-full accent-red-600"
                  />
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};


